import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LangContext";
import { pickLang, t } from "../lib/i18n";
import { api } from "../lib/api";
import { ArrowRight, ArrowUpRight, Lightning, Waveform, MicrophoneStage, VinylRecord, Buildings, Toolbox, CaretRight, InstagramLogo, FacebookLogo, SecurityCamera, VideoCamera, WifiHigh, Headphones } from "@phosphor-icons/react";
import MarqueeBand from "../components/MarqueeBand";
import PartnerCarousel from "../components/PartnerCarousel";
import ProductCard from "../components/ProductCard";
import SEO from "../components/SEO";

const ICONS = { Waveform, Lightning, Toolbox, Buildings, VinylRecord, MicrophoneStage, SecurityCamera, VideoCamera, WifiHigh, Headphones };

export default function Home() {
  const { lang } = useLang();
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [portfolio, setPortfolio] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/products?featured=true").then((r) => setProducts(r.data.slice(0, 6)));
    api.get("/portfolio").then((r) => setPortfolio(r.data.slice(0, 4)));
  }, []);

  return (
    <div data-testid="home-page">
      <SEO path="/" />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/50590957-5819-4a6e-b3ee-9ef342eeb207/images/efc9fd2474b62ac617f26b3a3afadecf347bb2ab97e9ebc758a2dcad9e294cbf.png"
            alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#08080a] via-transparent to-[#08080a]" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pt-20 pb-28 lg:pt-32 lg:pb-40">
          <div className="flex items-center gap-3 mb-6 fade-up">
            <div className="eq"><span/><span/><span/><span/></div>
            <span className="mono-font text-xs tracking-[0.3em] uppercase text-[#ccff00]">{t("hero.eyebrow", lang)}</span>
          </div>
          <h1 className="display-font text-5xl sm:text-7xl lg:text-[8rem] font-black uppercase leading-[0.9] tracking-tighter fade-up" data-testid="hero-title">
            {t("hero.title1", lang)}<br/>
            <span className="neon-text flicker">{t("hero.title2", lang)}</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mt-8 fade-up">{t("hero.sub", lang)}</p>
          <div className="flex flex-wrap gap-4 mt-10 fade-up">
            <Link to="/quote" className="btn-primary" data-testid="hero-quote-btn">
              {t("cta.request_quote", lang)} <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/products" className="btn-outline" data-testid="hero-explore-btn">
              {t("cta.explore", lang)}
            </Link>
          </div>

          {/* Social */}
          <div className="flex items-center gap-5 mt-10 fade-up" data-testid="hero-socials">
            <span className="label-tag">{lang === "gr" ? "Ακολουθήστε" : "Follow"}</span>
            <span className="h-px w-10 bg-[#2a2a35]" />
            <a
              href="https://www.instagram.com/aa_mpsound?igsh=MTJ3am5hbDEyNmdhbQ=="
              target="_blank" rel="noreferrer"
              data-testid="hero-instagram-btn"
              aria-label="Instagram"
              className="group flex items-center gap-2 border border-[#2a2a35] hover:border-[#ccff00] hover:text-[#ccff00] transition-all px-4 py-2.5"
            >
              <InstagramLogo size={18} weight="bold" />
              <span className="mono-font text-xs uppercase tracking-[0.2em]">Instagram</span>
            </a>
            <a
              href="https://www.facebook.com/aampsound"
              target="_blank" rel="noreferrer"
              data-testid="hero-facebook-btn"
              aria-label="Facebook"
              className="group flex items-center gap-2 border border-[#2a2a35] hover:border-[#ccff00] hover:text-[#ccff00] transition-all px-4 py-2.5"
            >
              <FacebookLogo size={18} weight="bold" />
              <span className="mono-font text-xs uppercase tracking-[0.2em]">Facebook</span>
            </a>
          </div>
        </div>

        {/* Floating stats */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 pb-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { v: "25+", l: { en: "Years on the road", gr: "Χρόνια εμπειρίας" } },
            { v: "500+", l: { en: "Events powered", gr: "Εκδηλώσεις" } },
            { v: "120k W", l: { en: "PA in our racks", gr: "PA στα racks μας" } },
            { v: "24/7", l: { en: "Pro support", gr: "Υποστήριξη" } },
          ].map((s, i) => (
            <div key={i} className="card-dark p-5">
              <div className="display-font text-3xl font-black neon-text">{s.v}</div>
              <div className="label-tag mt-2">{pickLang(s.l, lang)}</div>
            </div>
          ))}
        </div>
      </section>

      <MarqueeBand words={["SOUND", "LIGHT", "LASERS", "REPAIRS", "CCTV", "SECURITY", "INSTALL", "PRO AV"]} />

      <PartnerCarousel />

      {/* Services */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24" data-testid="home-services">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="label-tag mb-3 slash">{t("sections.services_eyebrow", lang)}</div>
            <h2 className="display-font text-4xl sm:text-5xl font-black uppercase tracking-tight max-w-2xl">
              {lang === "gr" ? "Όλα όσα χρειάζεται η νύχτα σας." : "Everything your night needs."}
            </h2>
          </div>
          <Link to="/services" className="text-sm mono-font uppercase tracking-[0.2em] text-[#ccff00] flex items-center gap-2">
            {t("cta.view_all", lang)} <CaretRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Ic = ICONS[s.icon] || Waveform;
            return (
              <div key={s.id} className="card-dark p-7 group" data-testid={`service-card-${i}`}>
                <Ic size={36} weight="duotone" className="text-[#ccff00] mb-5" />
                <h3 className="display-font text-xl font-bold mb-3">{pickLang(s.title, lang)}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{pickLang(s.description, lang)}</p>
                <Link to="/services" className="inline-flex items-center gap-1 mt-5 mono-font text-xs uppercase tracking-[0.2em] text-white group-hover:text-[#ccff00]">
                  {t("cta.learn_more", lang)} <ArrowUpRight size={14} />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* CCTV Spotlight */}
      <section className="relative border-y border-white/5 bg-gradient-to-b from-[#08080a] via-[#0d0d12] to-[#08080a] overflow-hidden" data-testid="cctv-spotlight">
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
             style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #ccff00 1px, transparent 1px), radial-gradient(circle at 80% 70%, #00f0ff 1px, transparent 1px)", backgroundSize: "60px 60px, 80px 80px" }} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <SecurityCamera size={28} weight="duotone" className="text-[#ccff00]" />
                <span className="mono-font text-xs tracking-[0.3em] uppercase text-[#ccff00]">{lang === "gr" ? "ΝΕΑ ΥΠΗΡΕΣΙΑ // CCTV & ΑΣΦΑΛΕΙΑ" : "NEW SERVICE // CCTV & SECURITY"}</span>
              </div>
              <h2 className="display-font text-4xl sm:text-6xl font-black uppercase leading-[0.92] tracking-tighter">
                {lang === "gr" ? <>Δείτε τα. <span className="neon-text">Κάθε γωνία.</span></> : <>Eyes on. <span className="neon-text">Every corner.</span></>}
              </h2>
              <p className="text-gray-300 mt-6 text-lg leading-relaxed max-w-2xl">
                {lang === "gr"
                  ? "Πλήρης σχεδιασμός, planning, υλοποίηση και εγκατάσταση CCTV — από μικρό κατάστημα μέχρι 100+ καμερών σε ξενοδοχεία, αποθήκες και clubs. 4K ανάλυση, AI analytics, mobile app, cloud backup."
                  : "Full design, planning, implementation and installation of CCTV — from a single shop to 100+ camera deployments across hotels, warehouses and clubs. 4K resolution, AI analytics, mobile app, cloud backup."}
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mt-10">
                {[
                  { tag: "01", title: { en: "WIRED", gr: "ΕΝΣΥΡΜΑΤΑ" }, sub: { en: "Coax & IP / PoE — maximum reliability and uptime.", gr: "Coax & IP / PoE — μέγιστη αξιοπιστία." } },
                  { tag: "02", title: { en: "WIRELESS", gr: "ΑΣΥΡΜΑΤΑ" }, sub: { en: "Wi-Fi & 4G — for hard-to-cable sites.", gr: "Wi-Fi & 4G — χωρίς καλωδίωση." } },
                  { tag: "03", title: { en: "HYBRID", gr: "ΥΒΡΙΔΙΚΑ" }, sub: { en: "Mix and match — the best of both worlds.", gr: "Συνδυασμός — ό,τι ταιριάζει στον χώρο σας." } },
                ].map((c, i) => (
                  <div key={i} className="border border-[#2a2a35] p-5 hover:border-[#ccff00] transition-colors" data-testid={`cctv-pillar-${i}`}>
                    <div className="mono-font text-[10px] tracking-[0.3em] text-gray-500">{c.tag}</div>
                    <div className="display-font font-black text-2xl mt-2 neon-text">{c.title[lang] || c.title.en}</div>
                    <p className="text-gray-400 text-xs mt-3 leading-relaxed">{c.sub[lang] || c.sub.en}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 mt-10">
                <Link to="/services" className="btn-primary" data-testid="cctv-services-btn">
                  {lang === "gr" ? "Δείτε τις υπηρεσίες" : "See our services"} <ArrowRight size={18} weight="bold" />
                </Link>
                <Link to="/products?category=cctv" className="btn-outline" data-testid="cctv-products-btn">
                  {lang === "gr" ? "Δείτε προϊόντα CCTV" : "Browse CCTV gear"}
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <div className="aspect-[4/5] bg-[#0a0a0c] border border-[#2a2a35] overflow-hidden relative">
                  <img
                    src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80"
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  {/* Grid overlay */}
                  <div className="absolute inset-0 pointer-events-none"
                       style={{ backgroundImage: "linear-gradient(rgba(204,255,0,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(204,255,0,0.07) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
                  {/* REC indicator */}
                  <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/70 backdrop-blur-sm px-3 py-1.5 mono-font text-[11px]">
                    <span className="w-2 h-2 bg-[#ff003c] rounded-full animate-pulse" />
                    <span className="text-white tracking-widest">REC · 4K · LIVE</span>
                  </div>
                  {/* Bottom timestamp */}
                  <div className="absolute bottom-4 left-4 mono-font text-[11px] text-[#ccff00] tracking-widest">
                    CAM-01 · {new Date().toLocaleDateString("en-GB")} · {new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 bg-[#ccff00] text-black p-4 max-w-[220px]">
                  <div className="display-font font-black text-lg leading-tight">{lang === "gr" ? "24/7 monitoring" : "24/7 monitoring"}</div>
                  <div className="mono-font text-[10px] uppercase tracking-widest mt-1">{lang === "gr" ? "Από οπουδήποτε" : "From anywhere"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="bg-[#0a0a0c] border-y border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="label-tag mb-3 slash">{t("sections.products_eyebrow", lang)}</div>
              <h2 className="display-font text-4xl sm:text-5xl font-black uppercase tracking-tight">
                {lang === "gr" ? "Φλαγκσιπ εξοπλισμός." : "Flagship gear."}
              </h2>
            </div>
            <Link to="/products" className="text-sm mono-font uppercase tracking-[0.2em] text-[#ccff00]">
              {t("cta.view_all", lang)} →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* Portfolio teaser */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="label-tag mb-3 slash">{t("sections.portfolio_eyebrow", lang)}</div>
            <h2 className="display-font text-4xl sm:text-5xl font-black uppercase tracking-tight">
              {lang === "gr" ? "Πρόσφατα έργα." : "Recent work."}
            </h2>
          </div>
          <Link to="/portfolio" className="text-sm mono-font uppercase tracking-[0.2em] text-[#ccff00]">
            {t("cta.view_all", lang)} →
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {portfolio.map((p, i) => (
            <Link key={p.id} to="/portfolio" className={`relative group overflow-hidden ${i % 3 === 0 ? "md:row-span-2" : ""}`} data-testid={`home-portfolio-${i}`}>
              <div className={`${i % 3 === 0 ? "aspect-[4/5]" : "aspect-[16/10]"} bg-[#0a0a0c]`}>
                <img src={p.image} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="mono-font text-xs tracking-[0.25em] uppercase text-[#ccff00]">{p.client} · {p.year}</div>
                <div className="display-font text-2xl font-bold mt-2">{pickLang(p.title, lang)}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="border-y border-white/5 bg-[#0a0a0c]">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h3 className="display-font text-3xl sm:text-5xl font-black uppercase tracking-tight">
            {lang === "gr" ? "Σχεδιάζετε εκδήλωση;" : "Got a show in the pipeline?"}
            <br/><span className="neon-text">{lang === "gr" ? "Ας μιλήσουμε." : "Let's talk."}</span>
          </h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/contact" className="btn-primary" data-testid="cta-contact-btn">
              {t("cta.send", lang)} <ArrowRight size={18} weight="bold" />
            </Link>
            <Link to="/booking" className="btn-outline" data-testid="cta-book-btn">
              {t("cta.book_now", lang)}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
